import React, { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Target,
  Users,
} from "lucide-react";
import { ChartResponsiveContainer } from "../../../components/ChartResponsiveContainer";
import { COLORS } from "../../../constants";
import type {
  RelatorioAssinaturaAgregado,
  RelatorioAssinaturasProducaoResponse,
  StatusAssinaturaMeta,
} from "../../../services/controleProducaoService";
import {
  CardMetrica,
  CardMetricas,
  ListaMobile,
  TabelaDesktop,
} from "./tabelaResponsiva";

const COR_NO_PRAZO = COLORS.SUCCESS;
const COR_ATRASADO = COLORS.ERROR_DARK;
const COR_NAO = COLORS.NEUTRAL_500;
const COR_PENDENTE = COLORS.WARNING_DARK;

type FiltroStatus = "atrasado" | "todos" | StatusAssinaturaMeta;

const APOS_LIMITE = "Após 16h (sáb. 17h)";

const FILTRO_OPTIONS: { id: FiltroStatus; label: string }[] = [
  { id: "atrasado", label: APOS_LIMITE },
  { id: "todos", label: "Todos" },
  { id: "no_prazo", label: "No prazo" },
  { id: "nao_assinado", label: "Não assinados" },
  { id: "pendente", label: "Pendentes" },
];

const STATUS_LABEL: Record<StatusAssinaturaMeta, string> = {
  no_prazo: "No prazo",
  atrasado: APOS_LIMITE,
  nao_assinado: "Não assinado",
  pendente: "Pendente",
};

function horaCurta(hhmm: string): string {
  const [h, m] = hhmm.split(":");
  return m && m !== "00" ? `${Number(h)}h${m}` : `${Number(h)}h`;
}

function statusLabel(status: StatusAssinaturaMeta, horaLimite: string): string {
  return status === "atrasado"
    ? `Após ${horaCurta(horaLimite)}`
    : STATUS_LABEL[status];
}

const STATUS_CLASS: Record<StatusAssinaturaMeta, string> = {
  no_prazo: "bg-emerald-50 text-emerald-800",
  atrasado: "bg-red-50 text-red-800",
  nao_assinado: "bg-neutral-100 text-neutral-700",
  pendente: "bg-amber-50 text-amber-900",
};

function dataFichaBR(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function TabelaPeriodo({
  rows,
}: {
  rows: RelatorioAssinaturaAgregado[];
}) {
  if (rows.length === 0) return null;
  return (
    <table className="mt-3 w-full min-w-0 text-xs">
      <thead>
        <tr className="text-neutral-500">
          <th className="py-1 text-left font-medium">Período</th>
          <th className="py-1 text-right font-medium">Meta</th>
          <th className="py-1 text-right font-medium">{APOS_LIMITE}</th>
          <th className="py-1 text-right font-medium">Sem assinar</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.periodoKey} className="border-t border-neutral-100">
            <td className="break-words py-1.5 pr-2 text-neutral-700">{r.periodoLabel}</td>
            <td className="py-1 text-right tabular-nums font-semibold text-neutral-800">
              {r.percentualNoPrazoFormatado}
            </td>
            <td className="py-1 text-right tabular-nums text-red-700">
              {r.atrasados}
            </td>
            <td className="py-1 text-right tabular-nums text-neutral-500">
              {r.naoAssinados}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function truncar(nome: string, max: number): string {
  const t = nome.trim();
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
}

function chartRows(rows: RelatorioAssinaturaAgregado[]) {
  return rows.map((r) => ({
    key: r.periodoKey,
    name: truncar(r.periodoLabel, 12),
    fullName: r.periodoLabel,
    meta: r.percentualNoPrazo ?? 0,
    metaLabel: r.percentualNoPrazoFormatado,
    noPrazo: r.noPrazo,
    atrasados: r.atrasados,
    naoAssinados: r.naoAssinados,
    pendentes: r.pendentes,
  }));
}

type StackTooltipProps = {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value?: number;
    color?: string;
    payload?: { fullName?: string; metaLabel?: string };
  }>;
};

const StackTooltip = ({ active, payload }: StackTooltipProps) => {
  if (!active || !payload?.length) return null;
  const titulo = payload[0]?.payload?.fullName;
  const meta = payload[0]?.payload?.metaLabel;
  return (
    <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-neutral-900">{titulo}</p>
      {meta ? (
        <p className="mt-0.5 text-neutral-500">Meta no prazo: {meta}</p>
      ) : null}
      <ul className="mt-1 space-y-0.5">
        {payload.map((p) => (
          <li key={p.name} className="flex items-center gap-2">
            <span
              className="inline-block h-2 w-2 rounded-sm"
              style={{ background: p.color }}
            />
            <span className="text-neutral-600">{p.name}</span>
            <span className="ml-auto tabular-nums font-semibold text-neutral-800">
              {p.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

type MetaAssinaturaTabProps = {
  data: RelatorioAssinaturasProducaoResponse;
};

const MetaAssinaturaTab: React.FC<MetaAssinaturaTabProps> = ({ data }) => {
  const [filtroStatus, setFiltroStatus] = useState<FiltroStatus>("atrasado");
  const [filtroSetor, setFiltroSetor] = useState<number | "">("");
  const [filtroUsuario, setFiltroUsuario] = useState<number | "">("");

  const resumo = data.resumo;
  const chartDia = useMemo(() => chartRows(data.porDia), [data.porDia]);
  const chartSemana = useMemo(
    () => chartRows(data.porSemana),
    [data.porSemana]
  );

  const linhas = useMemo(() => {
    return data.linhas.filter((l) => {
      if (filtroStatus !== "todos" && l.status !== filtroStatus) return false;
      if (filtroSetor !== "" && l.setorId !== filtroSetor) return false;
      if (filtroUsuario !== "" && l.userId !== filtroUsuario) return false;
      return true;
    });
  }, [data.linhas, filtroStatus, filtroSetor, filtroUsuario]);

  const setoresOpts = data.porSetor;
  const usuarioOpts = data.porUsuario;

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-neutral-200 bg-white p-4 sm:p-5">
        <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-neutral-900">
          <Target className="h-4 w-4 text-primary" />
          Meta: assinar PRODUÇÃO até {data.horaLimite} — exceto sábado, até{" "}
          {data.horaLimiteSabado}
        </p>
        <p className="text-xs leading-relaxed text-neutral-500">
          Cada setor da ficha do dia precisa assinar até as {data.horaLimite}{" "}
          (Brasília). Exceção: aos sábados o limite é {data.horaLimiteSabado}.
          Quem assina depois, ou não assina, conta contra a meta. Setor completo =
          todos os itens do setor resolvidos; incompleto = assinou com item
          pendente (liberação). Pendentes do dia atual (antes do horário-limite)
          não entram no percentual.
        </p>
      </section>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <p className="flex items-center gap-1.5 text-xs font-medium uppercase text-emerald-800">
            <Target className="h-3.5 w-3.5" />
            Meta no prazo
          </p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-emerald-900">
            {resumo.percentualNoPrazoFormatado}
          </p>
          <p className="mt-0.5 text-xs text-emerald-800/80">
            {resumo.noPrazo} de {resumo.esperados - resumo.pendentes} setores
          </p>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <p className="flex items-center gap-1.5 text-xs font-medium uppercase text-neutral-500">
            <CheckCircle2 className="h-3.5 w-3.5" />
            No prazo
          </p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-neutral-900">
            {resumo.noPrazo}
          </p>
          <p className="mt-0.5 text-xs text-neutral-500">
            {resumo.assinados} assinados no período
          </p>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="flex items-center gap-1.5 text-xs font-medium uppercase text-red-800">
            <Clock3 className="h-3.5 w-3.5" />
            {APOS_LIMITE}
          </p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-red-900">
            {resumo.atrasados}
          </p>
          <p className="mt-0.5 text-xs text-red-800/80">
            {resumo.naoAssinados} sem assinatura
          </p>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <p className="flex items-center gap-1.5 text-xs font-medium uppercase text-neutral-500">
            <AlertTriangle className="h-3.5 w-3.5" />
            Incompletos
          </p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-neutral-900">
            {resumo.incompletos}
          </p>
          <p className="mt-0.5 text-xs text-neutral-500">
            assinaram com item pendente
          </p>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <p className="flex items-center gap-1.5 text-xs font-medium uppercase text-neutral-500">
            <CalendarDays className="h-3.5 w-3.5" />
            Esperados
          </p>
          <p className="mt-1 text-2xl font-bold tabular-nums text-neutral-900">
            {resumo.esperados}
          </p>
          <p className="mt-0.5 text-xs text-neutral-500">
            {resumo.pendentes
              ? `${resumo.pendentes} ainda no prazo de hoje`
              : "setores × dias com ficha"}
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border border-neutral-200 bg-white p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-neutral-900">Por dia</h3>
          <p className="mt-0.5 text-xs text-neutral-500">
            Quantidade de setores no prazo, atrasados e sem assinatura.
          </p>
          <div className="relative mt-4 h-[18rem] w-full min-w-0">
            {chartDia.length === 0 ? (
              <p className="pt-16 text-center text-sm text-neutral-400">
                Nenhuma ficha no período.
              </p>
            ) : (
              <ChartResponsiveContainer initialHeightPx={288}>
                <BarChart data={chartDia} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip content={<StackTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar
                    dataKey="noPrazo"
                    name="No prazo"
                    stackId="a"
                    fill={COR_NO_PRAZO}
                  />
                  <Bar
                    dataKey="atrasados"
                    name={APOS_LIMITE}
                    stackId="a"
                    fill={COR_ATRASADO}
                  />
                  <Bar
                    dataKey="naoAssinados"
                    name="Não assinado"
                    stackId="a"
                    fill={COR_NAO}
                  />
                  <Bar
                    dataKey="pendentes"
                    name="Pendente"
                    stackId="a"
                    fill={COR_PENDENTE}
                  />
                </BarChart>
              </ChartResponsiveContainer>
            )}
          </div>
          <TabelaPeriodo rows={data.porDia} />
        </section>

        <section className="rounded-lg border border-neutral-200 bg-white p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-neutral-900">Por semana</h3>
          <p className="mt-0.5 text-xs text-neutral-500">
            Mesma meta agrupada pela segunda-feira da semana.
          </p>
          <div className="relative mt-4 h-[18rem] w-full min-w-0">
            {chartSemana.length === 0 ? (
              <p className="pt-16 text-center text-sm text-neutral-400">
                Nenhuma ficha no período.
              </p>
            ) : (
              <ChartResponsiveContainer initialHeightPx={288}>
                <BarChart data={chartSemana} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip content={<StackTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar
                    dataKey="noPrazo"
                    name="No prazo"
                    stackId="a"
                    fill={COR_NO_PRAZO}
                  />
                  <Bar
                    dataKey="atrasados"
                    name={APOS_LIMITE}
                    stackId="a"
                    fill={COR_ATRASADO}
                  />
                  <Bar
                    dataKey="naoAssinados"
                    name="Não assinado"
                    stackId="a"
                    fill={COR_NAO}
                  />
                  <Bar
                    dataKey="pendentes"
                    name="Pendente"
                    stackId="a"
                    fill={COR_PENDENTE}
                  />
                </BarChart>
              </ChartResponsiveContainer>
            )}
          </div>
          <TabelaPeriodo rows={data.porSemana} />
        </section>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
          <div className="border-b border-neutral-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-neutral-900">
              Meta por setor
            </h3>
          </div>
          <table className="w-full min-w-0 text-sm">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                  Setor
                </th>
                <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                  Meta
                </th>
                <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                  {APOS_LIMITE}
                </th>
                <th className="hidden px-3 py-2 text-right font-semibold text-neutral-600 sm:table-cell">
                  Incompleto
                </th>
              </tr>
            </thead>
            <tbody>
              {data.porSetor.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-3 py-6 text-center text-neutral-400"
                  >
                    Sem setores no período.
                  </td>
                </tr>
              ) : (
                data.porSetor.map((s) => (
                  <tr key={s.setorId} className="border-t border-neutral-100">
                    <td className="break-words px-3 py-2 font-medium text-neutral-800">
                      {s.setorNome}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {s.percentualNoPrazoFormatado}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-red-700">
                      {s.atrasados}
                    </td>
                    <td className="hidden px-3 py-2 text-right tabular-nums text-amber-800 sm:table-cell">
                      {s.incompletos}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
          <div className="border-b border-neutral-100 px-4 py-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
              <Users className="h-4 w-4 text-neutral-400" />
              Quem assinou após 16h (sáb. 17h)
            </h3>
          </div>
          <table className="w-full min-w-0 text-sm">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                  Pessoa
                </th>
                <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                  No prazo
                </th>
                <th className="px-3 py-2 text-right font-semibold text-neutral-600">
                  {APOS_LIMITE}
                </th>
                <th className="hidden px-3 py-2 text-right font-semibold text-neutral-600 sm:table-cell">
                  Atraso médio
                </th>
              </tr>
            </thead>
            <tbody>
              {data.porUsuario.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-3 py-6 text-center text-neutral-400"
                  >
                    Nenhuma assinatura no período.
                  </td>
                </tr>
              ) : (
                data.porUsuario.map((u) => (
                  <tr key={u.userId} className="border-t border-neutral-100">
                    <td className="break-words px-3 py-2 font-medium text-neutral-800">
                      {u.userNome}
                      {u.incompletos > 0 ? (
                        <span className="ml-2 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                          {u.incompletos} incompleto
                          {u.incompletos === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {u.percentualNoPrazoFormatado}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-red-700">
                      {u.atrasados}
                    </td>
                    <td className="hidden px-3 py-2 text-right tabular-nums text-neutral-600 sm:table-cell">
                      {u.atrasoMedioFormatado ?? "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-neutral-100 px-3 py-3 sm:px-4 lg:flex-row lg:flex-wrap lg:items-end">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-neutral-900">
              Detalhe por ficha e setor
            </h3>
            <p className="text-xs text-neutral-500">
              Quem assinou, horário, se o setor estava completo e se a ficha
              inteira fechou.
            </p>
          </div>
          <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3 lg:w-auto">
            <select
              value={filtroStatus}
              onChange={(e) =>
                setFiltroStatus(e.target.value as FiltroStatus)
              }
              className="h-11 w-full min-w-0 rounded-md border border-neutral-300 bg-white px-2 text-base lg:h-auto lg:py-1.5 lg:text-xs"
            >
              {FILTRO_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              value={filtroSetor}
              onChange={(e) =>
                setFiltroSetor(e.target.value ? Number(e.target.value) : "")
              }
              className="h-11 w-full min-w-0 rounded-md border border-neutral-300 bg-white px-2 text-base lg:h-auto lg:py-1.5 lg:text-xs"
            >
              <option value="">Todos os setores</option>
              {setoresOpts.map((s) => (
                <option key={s.setorId} value={s.setorId}>
                  {s.setorNome}
                </option>
              ))}
            </select>
            <select
              value={filtroUsuario}
              onChange={(e) =>
                setFiltroUsuario(e.target.value ? Number(e.target.value) : "")
              }
              className="h-11 w-full min-w-0 rounded-md border border-neutral-300 bg-white px-2 text-base lg:h-auto lg:py-1.5 lg:text-xs"
            >
              <option value="">Todas as pessoas</option>
              {usuarioOpts.map((u) => (
                <option key={u.userId} value={u.userId}>
                  {u.userNome}
                </option>
              ))}
            </select>
          </div>
        </div>
        {linhas.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-neutral-400">
            Nenhum registro com esses filtros.
          </p>
        ) : (
          <>
            <ListaMobile>
              {linhas.map((l) => (
                <article
                  key={`${l.dataFicha}-${l.setorId}`}
                  className="px-3 py-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="min-w-0 text-sm font-semibold text-neutral-900">
                      {l.setorNome}
                    </p>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLASS[l.status]}`}
                    >
                      {statusLabel(l.status, l.horaLimite)}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-neutral-500">
                    {dataFichaBR(l.dataFicha)} · {l.diaSemanaLabel}
                    {l.horaLimite !== data.horaLimite
                      ? ` (limite ${l.horaLimite})`
                      : ""}
                    {l.userNome ? ` · ${l.userNome}` : ""}
                  </p>
                  <CardMetricas>
                    <CardMetrica label="Horário">
                      {l.horarioAssinatura ?? "—"}
                      {l.minutosAposLimiteFormatado ? (
                        <span className="ml-1 text-red-700">
                          +{l.minutosAposLimiteFormatado}
                        </span>
                      ) : null}
                    </CardMetrica>
                    <CardMetrica label="Setor">
                      <span
                        className={
                          l.setorCompleto
                            ? "text-emerald-700"
                            : "text-amber-800"
                        }
                      >
                        {l.setorCompleto ? "Completo" : "Incompleto"}
                      </span>
                      <span className="ml-1 font-normal text-neutral-400">
                        {l.itensResolvidos}/{l.itensTotal}
                      </span>
                    </CardMetrica>
                    <CardMetrica label="Ficha" className="col-span-2">
                      {l.fichaProducaoCompleta ? (
                        <span className="text-emerald-700">Completa</span>
                      ) : (
                        <span className="text-neutral-500">Parcial</span>
                      )}
                    </CardMetrica>
                  </CardMetricas>
                </article>
              ))}
            </ListaMobile>
            <TabelaDesktop>
              <table className="w-full min-w-[52rem] text-sm">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                      Data
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                      Setor
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                      Quem assinou
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                      Horário
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                      Completo
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-neutral-600">
                      Ficha
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l) => (
                    <tr
                      key={`${l.dataFicha}-${l.setorId}`}
                      className="border-t border-neutral-100"
                    >
                      <td className="px-3 py-2 tabular-nums text-neutral-700">
                        <span className="font-medium">
                          {dataFichaBR(l.dataFicha)}
                        </span>
                        <span className="ml-1 text-xs text-neutral-400">
                          {l.diaSemanaLabel}
                          {l.horaLimite !== data.horaLimite
                            ? ` · limite ${l.horaLimite}`
                            : ""}
                        </span>
                      </td>
                      <td className="break-words px-3 py-2 font-medium text-neutral-800">
                        {l.setorNome}
                      </td>
                      <td className="px-3 py-2 text-neutral-700">
                        {l.userNome ?? "—"}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-neutral-700">
                        {l.horarioAssinatura ?? "—"}
                        {l.minutosAposLimiteFormatado ? (
                          <span className="ml-1 text-xs text-red-700">
                            +{l.minutosAposLimiteFormatado}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLASS[l.status]}`}
                        >
                          {statusLabel(l.status, l.horaLimite)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`text-xs font-semibold ${
                            l.setorCompleto
                              ? "text-emerald-700"
                              : "text-amber-800"
                          }`}
                        >
                          {l.setorCompleto ? "Completo" : "Incompleto"}
                        </span>
                        <span className="ml-1 text-xs tabular-nums text-neutral-400">
                          {l.itensResolvidos}/{l.itensTotal}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs font-semibold">
                        {l.fichaProducaoCompleta ? (
                          <span className="text-emerald-700">Completa</span>
                        ) : (
                          <span className="text-neutral-500">Parcial</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TabelaDesktop>
          </>
        )}
        <p className="border-t border-neutral-100 px-4 py-2 text-xs text-neutral-400">
          {linhas.length} registro{linhas.length === 1 ? "" : "s"}
        </p>
      </div>
    </div>
  );
};

export default MetaAssinaturaTab;
